import React, { createContext, useMemo } from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import moment from 'moment';
import set from 'lodash/set';
import styled from 'styled-components';

import EditableIvrName from './components/EditableIvrName';
import HelpButton, { HelpButtonPosition } from './components/HelpButton';
import IvrEditor from './IvrEditor';
import TableLayout from 'src/presentation/components/layouts/table/TableLayout';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import useRouter from 'src/presentation/hooks/useRouter';
import useSaveIvrName from './hooks/useSaveIvrName';
import useArchiveIvr from './hooks/useArchiveIvr';
import { CloseButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { Archive } from 'src/presentation/components/pages/shared/rowActions/Archive';
import { CopyIvr } from 'src/presentation/components/pages/ivr/components/rowActions/CopyIvr';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';
import { LongTextWithTooltip } from 'src/presentation/components/pages/shared/LongTextWithTooltip';
import { STATUS_LOADING } from 'src/presentation/hooks/useResource';
import { useIvrsWithPhonesAndRecordings } from 'src/presentation/components/pages/shared/hooks/useIvrsWithPhones';
import useCreateNewIvr from './hooks/useCreateNewIvr';

export const IvrContext = createContext<{ ivrConfigurations: any[]; voiceEndpoints: any[]; history: any }>({
  ivrConfigurations: [],
  voiceEndpoints: [],
  history: {},
});

const columnsForEditor = [
  {
    attr: 'publishedAt',
    label: 'Last Published',
    customRenderer: item => {
      return item.publishedAt ? moment.utc(item.publishedAt).local().format('MM/DD/YYYY hh:mm A') : 'Never Published';
    },
    customColumnStyle: () => ({ width: '170px' }),
  },
  {
    attr: 'publishedByAgent',
    label: 'Published by',
    customColumnStyle: () => ({ width: '170px' }),
    customRenderer: item => {
      return <LongTextWithTooltip value={get(item, 'publishedByAgent', '')} />;
    },
  },
  {
    attr: 'calc_publishedAt',
    label: 'Published Change',
    customColumnStyle: () => ({ width: '200px' }),
    customRenderer: item => {
      const value: string = get(item, 'publishedAt') ? item.comment : '';
      return <LongTextWithTooltip value={value} />;
    },
  },
  {
    attr: 'Notes',
    label: 'Notes',
    customRenderer: item =>
      get(item, 'ivrHasChanges', true) ? <StyledNotes>Unpublished draft exists</StyledNotes> : null,
  },
];

const searchFuncForIvrs = (items, searchString) => {
  if (!searchString) {
    return items;
  }
  return filter(
    items,
    item =>
      item.id.toLowerCase().includes(searchString.toLowerCase()) ||
      get(item, 'name', '').toLowerCase().includes(searchString.toLowerCase()) ||
      get(item, 'description', '').toLowerCase().includes(searchString.toLowerCase())
  );
};

enum IVRS_TABS {
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  ARCHIVE = 'archive',
}

const editorConfig: any[] = [
  {
    itemsKey: IVRS_TABS.ACTIVE,
    editor: IvrEditor,
    closeButtonType: CloseButtonTypes.BACK_BUTTON,
    closeButtonLabel: 'IVRs',
    topPadding: 0,
  },
];

const TABS_MAPPER = {
  [IVRS_TABS.ACTIVE]: {
    label: 'Active',
    key: IVRS_TABS.ACTIVE,
    itemsKey: IVRS_TABS.ACTIVE,
  },
  [IVRS_TABS.INACTIVE]: {
    label: 'Inactive',
    key: IVRS_TABS.INACTIVE,
    itemsKey: IVRS_TABS.INACTIVE,
  },
  [IVRS_TABS.ARCHIVE]: {
    label: 'Archived',
    key: IVRS_TABS.ARCHIVE,
    itemsKey: IVRS_TABS.ARCHIVE,
  },
};

const SELECTED_TAB_WITH_FILTER_REGEX = /^#(?<currentTab>\w+)?(\?search=(?<search>[\w+.-]+))?/i;

const SEARCH_REGEX = /^(\?search=(?<search>[\w+.-]+))/i;

const createTabsConfig = (ivrsActive, ivrsInactive, ivrsArchive) => {
  return {
    [IVRS_TABS.ACTIVE]: ivrsActive,
    [IVRS_TABS.INACTIVE]: ivrsInactive,
    [IVRS_TABS.ARCHIVE]: ivrsArchive,
  };
};

const IvrConfigurations: TablePageRender = ({ root }) => {
  const { location, history } = useRouter();

  const allowedTabHashes = ['active', 'inactive', 'archive'];
  const locationHash = get(location, 'hash', '');
  const hashParams = get(locationHash.toLowerCase().match(SELECTED_TAB_WITH_FILTER_REGEX), 'groups', {});

  const search = locationHash
    ? get(hashParams, 'search', '')
    : get(get(location, 'search', '').toLowerCase().match(SEARCH_REGEX), 'groups.search', '');

  let tabFromHash = get(hashParams, 'currentTab', '');

  if (tabFromHash && !includes(allowedTabHashes, tabFromHash)) {
    tabFromHash = IVRS_TABS.ACTIVE;
  }

  const preselectedTab = tabFromHash;

  const {
    agents,
    errors: ivrsErrors,
    ivrConfigurations,
    ivrs,
    ivrsIdNameMap,
    loadingState: ivrsLoadingState,
    setIvrsIdNameMap,
    voiceEndpoints,
  } = useIvrsWithPhonesAndRecordings();
  const isLoading = ivrsLoadingState === STATUS_LOADING;

  ivrs.forEach(ivr => {
    if (get(ivr, 'publishedAt')) {
      let agentName = '';
      const agentId = get(ivr, 'publishedBy');
      if (agentId) {
        const agent = find(agents, agent => agent.id === agentId);
        agentName = agent ? get(agent, 'name') : 'Deleted User';
      }
      set(ivr, 'publishedByAgent', agentName);
    }
  });

  const items = createTabsConfig(ivrs, ivrs, ivrs);
  const errors = createTabsConfig(ivrsErrors, ivrsErrors, ivrsErrors);
  const resetErrors = createTabsConfig(
    ivrsResource.actions.resetErrors,
    ivrsResource.actions.resetErrors,
    ivrsResource.actions.resetErrors
  );

  const onNameSave = useSaveIvrName(ivrsIdNameMap, setIvrsIdNameMap);

  const { archiveIvr, unarchiveIvr } = useArchiveIvr();

  const columnsForIVRs = useMemo(
    () => [
      {
        attr: 'id',
        label: 'Name',
        customColumnStyle: () => ({ width: '170px' }),
        customStyle: () => ({ position: 'relative' }),
        customRenderer: item => {
          const id = get(item, 'id', '');
          const name = get(ivrsIdNameMap, `[${id}].name`);

          return <EditableIvrName dataAid={id} onSave={newName => onNameSave(item, newName)} value={name} />;
        },
      },
    ],
    [ivrsIdNameMap, onNameSave]
  );

  const renderIvrRowActions = ({ item }) => (
    <>
      <CopyIvr item={item} errors={ivrsErrors} />
      <Edit id={item.id} dataAid={`editLink-${item.id}`} />
    </>
  );

  const renderInactiveIvrRowActions = ({ item }) => (
    <>
      <CopyIvr item={item} errors={ivrsErrors} />
      <Archive
        confirmationText={`Archive "${item.name || item.description}" ?`}
        description="Archiving this IVR will affect all its versions."
        submitText="Archive IVR"
        itemId={item.id}
        onArchive={() => archiveIvr(item)}
      />
      <Edit id={item.id} dataAid={`editLink-${item.id}`} />
    </>
  );

  const renderArchiedIvrRowActions = ({ item }) => (
    <>
      <Archive
        confirmationText="Unarchiving an IVR"
        description={
          <>
            This IVR will be moved to the inactive tab. In order to make this
            <br />
            IVR active, you must associate a phone number to it.
          </>
        }
        submitText="Unarchive"
        itemId={item.id}
        onArchive={() => unarchiveIvr(item)}
        isUnarchive={true}
      />
      <Edit id={item.id} />
    </>
  );

  const columnsForIVRsTab = [...columnsForIVRs, ...columnsForEditor];

  const getTabConfig = () => [
    {
      ...TABS_MAPPER[IVRS_TABS.ACTIVE],
      filter: item => item.isActive,
      filterPlaceholder: 'Search Active IVRs',
      columns: columnsForIVRsTab,
      searchFunc: searchFuncForIvrs,
      renderRowActions: renderIvrRowActions,
    },
    {
      ...TABS_MAPPER[IVRS_TABS.INACTIVE],
      filter: item => !item.isActive && !item.archivedAt,
      filterPlaceholder: 'Search Inactive IVRs',
      columns: columnsForIVRsTab,
      searchFunc: searchFuncForIvrs,
      renderRowActions: renderInactiveIvrRowActions,
    },
    {
      ...TABS_MAPPER[IVRS_TABS.ARCHIVE],
      filter: item => item.archivedAt,
      filterPlaceholder: 'Search Archived IVRs',
      columns: columnsForIVRsTab,
      searchFunc: searchFuncForIvrs,
      renderRowActions: renderArchiedIvrRowActions,
    },
  ];

  const tabConfig = getTabConfig();

  const contextValue = {
    ivrConfigurations,
    voiceEndpoints,
    history,
  };

  const { createNewIvrButton, newIvrModal } = useCreateNewIvr({ errors: ivrsErrors });
  const Description = (
    <>
      IVR (Interactive Voice Response) or as some call it, “Phone Tree”, allows customers go through when they call in
      to get help.{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://help.gladly.com/docs/whats-an-ivr">
        Learn more about IVR here
      </a>
    </>
  );

  return (
    <>
      <IvrContext.Provider value={contextValue}>
        <StyledTablePage
          actions={createNewIvrButton}
          className="ivrTableList"
          data-aid="admin-ivr-configuration-page"
          description={Description}
          initialSearchString={search}
          editor={editorConfig}
          errors={errors}
          isLoading={isLoading}
          items={items}
          name="IVR"
          preventCloseOnEscape
          resetErrors={resetErrors}
          root={root}
          preselectedTab={preselectedTab}
          tabConfig={tabConfig}
          updateUrlOnTabChange
        />
        <HelpButton position={HelpButtonPosition.BOTTOM_RIGHT} />
        {newIvrModal}
      </IvrContext.Provider>
    </>
  );
};

const StyledNotes = styled.span`
  color: ${p => p.theme.colors.gray900};
`;

const StyledTablePage = styled(TablePage)`
  ${TableLayout} {
    margin-bottom: 80px;
  }
`;

export default IvrConfigurations;
